/* ==========================================================================
   Kaufwerk – Filter & Sortierung ohne Neuladen (Section Rendering API)
   ========================================================================== */
(function () {
  'use strict';

  const theme = (window.theme = window.theme || {});
  const strings = theme.strings || {};

  const debounce = (fn, wait) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  const announce = (message) => {
    if (theme.utils && theme.utils.announce) theme.utils.announce(message);
  };

  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.form = this.querySelector('[data-facets-form]');
      this.drawer = this.querySelector('[data-facets-drawer]');
      this.openButton = this.querySelector('[data-facets-open]');
      this.debouncedSubmit = debounce(() => this.submit(), 700);

      this.addEventListener('change', (event) => {
        const target = event.target;
        if (target.matches('[data-price-input]')) {
          this.debouncedSubmit();
          return;
        }
        if (target.closest('[data-facets-form]') || target.matches('[data-sort-select]')) this.submit();
      });

      this.addEventListener('input', (event) => {
        if (event.target.matches('[data-price-input]')) this.debouncedSubmit();
      });

      this.addEventListener('submit', (event) => {
        if (event.target.matches('[data-facets-form]')) {
          event.preventDefault();
          this.submit();
        }
      });

      this.addEventListener('click', (event) => {
        const link = event.target.closest('[data-facet-link]');
        if (link) {
          event.preventDefault();
          const url = new URL(link.href, window.location.origin);
          this.load(url.searchParams.toString());
          return;
        }
        if (event.target.closest('[data-facets-open]')) {
          this.openDrawer();
          return;
        }
        if (event.target.closest('[data-facets-close]')) {
          this.closeDrawer();
          return;
        }
        const more = event.target.closest('[data-facet-more]');
        if (more) {
          const list = more.closest('.facet-list');
          const expanded = list.classList.toggle('is-expanded');
          more.setAttribute('aria-expanded', String(expanded));
          more.querySelector('[data-more-label]').hidden = expanded;
          more.querySelector('[data-less-label]').hidden = !expanded;
        }
      });

      this.onPopState = (event) => {
        const query = event.state && typeof event.state.facetQuery === 'string' ? event.state.facetQuery : window.location.search.slice(1);
        this.load(query, false);
      };
      window.addEventListener('popstate', this.onPopState);

      this.onKeydown = (event) => {
        if (!this.drawer || !this.drawer.classList.contains('is-open')) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeDrawer();
        }
        if (event.key === 'Tab') this.trapFocus(event);
      };
      document.addEventListener('keydown', this.onKeydown);

      this.mediaQuery = window.matchMedia('(min-width: 990px)');
      this.mediaQuery.addEventListener('change', () => {
        if (this.mediaQuery.matches) this.closeDrawer(true);
      });
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
      document.removeEventListener('keydown', this.onKeydown);
    }

    buildQuery() {
      if (!this.form) return '';
      const params = new URLSearchParams();
      new FormData(this.form).forEach((value, key) => {
        if (String(value).trim() !== '') params.append(key, value);
      });
      return params.toString();
    }

    submit() {
      this.load(this.buildQuery());
    }

    async load(query, push = true) {
      const container = this.querySelector('[data-product-grid-container]');
      container?.classList.add('is-loading');
      this.abortController?.abort();
      this.abortController = new AbortController();

      const base = window.location.pathname;
      const fetchUrl = `${base}?${query}${query ? '&' : ''}section_id=${encodeURIComponent(this.sectionId)}`;

      try {
        const response = await fetch(fetchUrl, { signal: this.abortController.signal });
        if (!response.ok) throw new Error(response.status);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        this.render(doc);
        if (push) window.history.pushState({ facetQuery: query }, '', `${base}${query ? '?' + query : ''}`);
        const count = doc.querySelector('[data-product-count]');
        announce(`${strings.filtersUpdated || ''} ${count ? count.textContent.trim() : ''}`.trim());
      } catch (error) {
        if (error.name !== 'AbortError') window.location.href = `${base}${query ? '?' + query : ''}`;
      } finally {
        container?.classList.remove('is-loading');
      }
    }

    render(doc) {
      const activeId = document.activeElement && document.activeElement.id;
      const openFacets = new Set(Array.from(this.querySelectorAll('details.facet[open]')).map((d) => d.dataset.facet));
      const expandedLists = new Set(
        Array.from(this.querySelectorAll('.facet-list.is-expanded')).map((list) => list.closest('details.facet')?.dataset.facet)
      );

      const replaceInner = (selector) => {
        const fresh = doc.querySelector(selector);
        const current = this.querySelector(selector);
        if (fresh && current) current.innerHTML = fresh.innerHTML;
      };

      replaceInner('[data-product-grid-container]');
      replaceInner('[data-facets-form]');
      replaceInner('.facets__footer');
      replaceInner('[data-active-facets]');

      const freshCount = doc.querySelector('[data-product-count]');
      const currentCount = this.querySelector('[data-product-count]');
      if (freshCount && currentCount) currentCount.textContent = freshCount.textContent;

      const freshBubble = doc.querySelector('[data-active-count]');
      const currentBubble = this.querySelector('[data-active-count]');
      if (freshBubble && currentBubble) {
        currentBubble.textContent = freshBubble.textContent;
        currentBubble.hidden = freshBubble.hasAttribute('hidden');
      }

      const freshSort = doc.querySelector('[data-sort-select]');
      const currentSort = this.querySelector('[data-sort-select]');
      if (freshSort && currentSort) currentSort.value = freshSort.value;

      this.querySelectorAll('details.facet').forEach((details) => {
        if (openFacets.has(details.dataset.facet)) details.setAttribute('open', '');
        if (expandedLists.has(details.dataset.facet)) {
          const list = details.querySelector('.facet-list');
          const more = details.querySelector('[data-facet-more]');
          list?.classList.add('is-expanded');
          if (more) {
            more.setAttribute('aria-expanded', 'true');
            more.querySelector('[data-more-label]').hidden = true;
            more.querySelector('[data-less-label]').hidden = false;
          }
        }
      });

      if (activeId) {
        const el = document.getElementById(activeId);
        if (el) el.focus({ preventScroll: true });
      }
    }

    /* ---------- Mobiler Filter-Drawer ---------- */
    openDrawer() {
      if (!this.drawer) return;
      this.lastFocus = document.activeElement;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('role', 'dialog');
      this.drawer.setAttribute('aria-modal', 'true');
      this.openButton?.setAttribute('aria-expanded', 'true');
      this.overlay = document.createElement('div');
      this.overlay.className = 'facets-overlay';
      this.overlay.addEventListener('click', () => this.closeDrawer());
      document.body.appendChild(this.overlay);
      document.documentElement.classList.add('has-overlay');
      window.setTimeout(() => this.drawer.querySelector('[data-facets-close]')?.focus(), 50);
    }

    closeDrawer(silent) {
      if (!this.drawer || !this.drawer.classList.contains('is-open')) return;
      this.drawer.classList.remove('is-open');
      this.drawer.removeAttribute('role');
      this.drawer.removeAttribute('aria-modal');
      this.openButton?.setAttribute('aria-expanded', 'false');
      this.overlay?.remove();
      if (!document.querySelector('dialog[open]')) document.documentElement.classList.remove('has-overlay');
      if (!silent && this.lastFocus && document.contains(this.lastFocus)) this.lastFocus.focus();
      else if (!silent) this.openButton?.focus();
    }

    trapFocus(event) {
      const focusable = Array.from(
        this.drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, summary')
      ).filter((el) => el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  if (!customElements.get('facet-filters')) customElements.define('facet-filters', FacetFilters);
})();
